import * as React from 'react';
import { css } from '@emotion/react';
import { Transition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';
import {
    FixMeLater,
    BaseTransitionProps,
    getAutoAnimationDuration,
    TransitionDuration,
    ReactElementWithRef,
} from '../../utils';
import { useForkRef } from '../../hooks';
import { useHoneycombTheme } from '../../theming';

export type ExpandOrientation = 'horizontal' | 'vertical';

export interface ExpandProps extends BaseTransitionProps {
    /**
     * A single child content element.
     */
    children: ReactElementWithRef<HTMLDivElement>;

    /**
     * The width (horizontal) or height (vertical) of the container when collapsed.
     * @default '0px'
     */
    collapsedSize?: string | number;

    /**
     * The orientation of the transition.
     * @default 'vertical'
     */
    orientation?: ExpandOrientation;

    /**
     * @ignore
     */
    style?: React.CSSProperties;

    /**
     * The duration for the transition, in milliseconds.
     */
    timeout?: 'auto' | TransitionDuration;
}

/**
 * Expand is a transitional component which can grow and expand outwards from the child element,
 * with a fade effect, over a certain timeout duration. Supports vertical and horizontal expansion.
 */
export function Expand(props: Readonly<ExpandProps>): React.JSX.Element {
    const { theme } = useHoneycombTheme();
    const { duration, easing } = theme.transitions;

    const {
        appear = false,
        children,
        collapsedSize: collapsedSizeProp = '0px',
        in: inProp,
        onEnter,
        onEntered,
        onEntering,
        onExit,
        onExited,
        onExiting,
        orientation = 'vertical',
        style,
        timeout = {
            enter: duration.standard,
            exit: duration.standard,
        },
        ref,
        ...other
    } = props;

    const collapsedSize = typeof collapsedSizeProp === 'number' ? `${collapsedSizeProp}px` : collapsedSizeProp;
    const isHorizontal = orientation === 'horizontal';
    const timer = React.useRef<number | null>(null);
    const autoTransitionDuration = React.useRef<number | null>(null);
    const size = isHorizontal ? 'width' : 'height';

    const wrapperRef = React.useRef<HTMLDivElement | null>(null);
    const nodeRef = React.useRef<HTMLDivElement>(null!);

    const handleRefIntermediary = useForkRef(children.ref, nodeRef);
    const handleRef = useForkRef(handleRefIntermediary, ref);

    const getWrapperSize = (): number => {
        return wrapperRef.current ? wrapperRef.current[isHorizontal ? 'clientWidth' : 'clientHeight'] : 0;
    };

    const handleEnter = (isAppearing: boolean): void => {
        const node = nodeRef.current;
        if (!node) return;

        if (wrapperRef.current && isHorizontal) {
            wrapperRef.current.style.position = 'absolute';
        }
        node.style[size] = collapsedSize;

        onEnter?.(isAppearing);
    };

    const handleEntering = (isAppearing: boolean): void => {
        const node = nodeRef.current;
        if (!node) return;

        const wrapperSize = getWrapperSize();

        if (wrapperRef.current && isHorizontal) {
            wrapperRef.current.style.position = '';
        }

        if (timeout === 'auto') {
            const durationValue = getAutoAnimationDuration(wrapperSize);
            node.style.transitionDuration = `${durationValue}ms`;
            autoTransitionDuration.current = durationValue;
        } else {
            node.style.transitionDuration = `${timeout.enter}ms`;
        }

        node.style[size] = `${wrapperSize}px`;
        node.style.transitionTimingFunction = easing.easeInOut;

        onEntering?.(isAppearing);
    };

    const handleEntered = (): void => {
        const node = nodeRef.current;
        if (!node) return;

        node.style[size] = 'auto';
        onEntered?.();
    };

    const handleExit = (): void => {
        const node = nodeRef.current;
        if (!node) return;

        node.style[size] = `${getWrapperSize()}px`;
        onExit?.();
    };

    const handleExiting = (): void => {
        const node = nodeRef.current;
        if (!node) return;

        const wrapperSize = getWrapperSize();

        if (timeout === 'auto') {
            const durationValue = getAutoAnimationDuration(wrapperSize);
            node.style.transitionDuration = `${durationValue}ms`;
            autoTransitionDuration.current = durationValue;
        } else {
            node.style.transitionDuration = `${timeout.exit}ms`;
        }

        node.style[size] = collapsedSize;
        node.style.transitionTimingFunction = easing.easeInOut;

        onExiting?.();
    };

    const handleAddEndListener = (next: TimerHandler): void => {
        if (timeout === 'auto') {
            timer.current = window.setTimeout(next, autoTransitionDuration.current || 0);
        }
    };

    const rootStyles = css`
        overflow: hidden;
        ${orientation === 'vertical' &&
        `
      height: 0;
      transition: height ${duration.standard}ms ${easing.easeInOut} 0ms;
    `}
        ${orientation === 'horizontal' &&
        `
      height: auto;
      width: 0;
      transition: width ${duration.standard}ms ${easing.easeInOut} 0ms;
    `}
    `;

    const wrapperStyles = css`
        display: flex;
        width: 100%;
        ${orientation === 'horizontal' &&
        `
      width: auto;
      height: 100%;
    `}
    `;

    const innerWrapperStyles = css`
        width: 100%;
        ${orientation === 'horizontal' &&
        `
      width: auto;
      height: 100%;
    `}
    `;

    return (
        <Transition
            nodeRef={nodeRef}
            in={inProp}
            appear={appear}
            timeout={timeout === 'auto' ? { enter: undefined, exit: undefined } : timeout}
            onEnter={handleEnter}
            onEntered={handleEntered}
            onEntering={handleEntering}
            onExit={handleExit}
            onExited={onExited}
            onExiting={handleExiting}
            addEndListener={handleAddEndListener}
            {...other}
        >
            {(status: TransitionStatus, childProps: (Partial<FixMeLater> & React.Attributes) | undefined) => {
                const rootStatusStyles = css`
                    ${status === 'entered' &&
                    `
            height: auto;
            overflow: visible;
          `}
                    ${status === 'exited' &&
                    !inProp &&
                    collapsedSize === '0px' &&
                    `
            visibility: hidden;
          `}
                `;
                return (
                    <div
                        ref={handleRef}
                        style={{
                            ...(isHorizontal ? { minWidth: collapsedSize } : { minHeight: collapsedSize }),
                            ...style,
                        }}
                        css={[rootStyles, rootStatusStyles]}
                        {...childProps}
                    >
                        <div ref={wrapperRef} css={wrapperStyles}>
                            <div css={innerWrapperStyles}>{children}</div>
                        </div>
                    </div>
                );
            }}
        </Transition>
    );
}
